'use client';
import { useMemo } from 'react';

import ScrollContainer from 'react-indiana-drag-scroll';
import { useParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import { useDevice } from '@contexts/device';
import { FlexibleDiv } from '@components/formStyles';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import Markdown from '@components/utils/Markdown';
import { callService } from '@api/remote-call';
import { extractURLFromIframeData } from '@utils/extractURLFromIframeData';
import { signUpGtm } from '@utils/gtm/membership-gtm';

import {
  Wrapper,
  BannerWrapperDetail,
  ContentWrapper,
  Banner,
  Description,
  PromotionContent,
  PromotionDescription,
  PromotionImage,
  PromotionItem,
  PromotionBorder,
  PromotionLayer,
  PromotionTitle,
  Label,
  ConditionsDot,
  ConditionsItem,
  ConditionsText,
  TextContentWrapper,
  PromotionItemsDetail,
  IframeWrapper,
} from './styled';

const ErrorPage404 = dynamic(
  () => import('@components/views/error/ErrorPage404')
);
const CampaignDetailSkeleton = dynamic(
  () => import('./CampaignDetailSkeleton')
);
const Title = dynamic(() => import('./DesktopCampaignDetail'));
const TitleMobile = dynamic(() => import('./MobileCampaignDetail'));

const CampaignDetail = () => {
  const params = useParams();

  const { data, isError, isLoading } = useQuery(['campaignDetail'], () =>
    callService(
      {
        service: 'ContentsService',
        method: 'getCampaignDetails',
        version: 'v3',
      },
      {
        slug: params?.slug as string,
      }
    )
  );
  const breadItems = useMemo(() => {
    const itemTitle = data?.imageAndTitle?.[0].name;

    return [
      {
        title: 'Ana Sayfa',
        url: '/',
      },
      {
        title: 'Kampanyalar',
        url: '/kampanyalar',
      },
      {
        title: itemTitle,
        url: `/${params?.slug}`,
      },
    ];
  }, [params, data]);
  const { isDesktop } = useDevice();

  const extractedURLs = useMemo(() => {
    return data?.promotions
      ?.map((it) => it?.videoCode)
      .map((iframeData) => extractURLFromIframeData(iframeData));
  }, [data]);

  const displayIframe = useMemo(() => {
    return extractedURLs?.every((url) => {
      return url !== null;
    });
  }, [extractedURLs]);

  const handleCTAClick = (url: string) => {
    if (params?.slug === 'setur-extra-hos-geldin-kampanyasi') {
      if (url.includes('uye-ol')) {
        signUpGtm('Hoşgeldin Kampanyası - Hemen Üye Ol');
      }
    }
    if (params?.slug === 'setur-extra-kampanyasi') {
      if (url.includes('uye-ol')) {
        signUpGtm('SeturExtra Kampanyası - Hemen Üye Ol');
      }
    }
  };

  if (isError) return <ErrorPage404 />;

  return (
    <Wrapper>
      {isLoading || !data ? (
        <CampaignDetailSkeleton />
      ) : (
        <ContentWrapper>
          <BannerWrapperDetail>
            <Banner
              src={data?.imageAndTitle?.[0].images?.[0]?.url}
              alt={data?.imageAndTitle?.[0].images?.[0]?.alt}
              fill
            />
          </BannerWrapperDetail>
          <TextContentWrapper>
            <FlexibleDiv top={0} bottom={0}>
              <Breadcrumb items={breadItems} />
            </FlexibleDiv>
            {isDesktop ? (
              <Title title={data?.imageAndTitle?.[0]?.name} />
            ) : (
              <TitleMobile title={data?.imageAndTitle?.[0]?.name} />
            )}

            <Description>
              <Markdown text={data?.description1?.[0]?.text} />
            </Description>
            {data?.promotions?.length > 0 ? (
              <ScrollContainer className='scroll-container'>
                <PromotionItemsDetail item={data?.promotions?.length}>
                  {data?.promotions.map((item) => (
                    <Link
                      key={item.url}
                      href={item?.url || ''}
                      onClick={() => handleCTAClick(item?.url ?? '')}
                      prefetch={false}
                    >
                      <PromotionItem>
                        <PromotionLayer />
                        <PromotionImage
                          src={item?.images[0]?.url}
                          alt={item?.images[0]?.url}
                          fill
                        />

                        <PromotionContent>
                          <PromotionTitle>{item.name}</PromotionTitle>
                          <PromotionBorder />
                          <PromotionDescription>
                            {item.priceSubText}
                          </PromotionDescription>
                        </PromotionContent>
                      </PromotionItem>
                    </Link>
                  ))}
                  {displayIframe ? (
                    <IframeWrapper>
                      <iframe
                        width='295'
                        height='270'
                        allowFullScreen
                        src={extractedURLs?.toString()}
                        title='YouTube Video Player'
                        frameBorder={0}
                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                      />
                    </IframeWrapper>
                  ) : null}
                </PromotionItemsDetail>
              </ScrollContainer>
            ) : null}
            <Label>{data.description2?.[0]?.title}</Label>
            <FlexibleDiv bottom={60}>
              {data.description2?.[0]?.text
                ?.split('- ')
                .filter((item) => item !== '')
                .map((item) => (
                  <ConditionsItem key={item}>
                    <ConditionsDot />

                    <ConditionsText>
                      <Markdown text={item} />
                    </ConditionsText>
                  </ConditionsItem>
                ))}
            </FlexibleDiv>
          </TextContentWrapper>
        </ContentWrapper>
      )}
    </Wrapper>
  );
};

export default CampaignDetail;
